'use client';

import React, { useState } from "react";
import { useError } from "../hooks/useError";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { 
    FormContainer, 
    HeaderContainer, 
    ButtonContainer, 
    Form, 
    Title, 
    Text 
} from "./styles";
import FontLoader, { GlobalStyles } from "../styles/global";

import { Input } from "../Input";
import { Button } from "../Button";
import { Alert } from "../Alert";

export interface MitreFormComponentProps {
  productId: string;
  apiUrl: string;
  apiToken: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  showHeader?: boolean;
  colorPrimary?: string;
  textColor?: string;
}

const schema = yup.object().shape({
  name: yup.string().required("Nome é obrigatório"),
  email: yup.string().required("Email é obrigatório").email("Email inválido"),
  phone: yup.string().required("Telefone é obrigatório")
  .test(
    'min-digits',
    'Número de telefone inválido!',
    (value) => {
      const digitsOnly = value?.replace(/\D/g, '') || '';
      //TODO melhorar essa lógica com algum regex
      return digitsOnly.length >= 8
    })
});

const MitreFormComponent = React.forwardRef<HTMLDivElement, MitreFormComponentProps>(({
  productId,
  apiUrl,
  apiToken,
  utm_source,
  utm_medium,
  utm_campaign,
  utm_term,
  showHeader =  true,
  colorPrimary = "#F6C76B",
  textColor = "#2F2F2F",
}, ref) => {
  const [loading, setIsLoading] = useState(false);
  const { error, handleError, clearError } = useError();
  const [successMessage, setSuccessMessage] = useState('');

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm({
    resolver: yupResolver(schema),
  });

  const phoneValue = watch("phone");

  const sendMessage: SubmitHandler<{ name: string; email: string; phone: string; }> = async (data) => {
    const { name, email, phone } = data;
    const message = "Gostaria de mais informações sobre o produto";

    try {
      setIsLoading(true);

      if (!productId || !utm_source || !utm_medium || !utm_campaign || !utm_term || !apiToken) {
        throw new Error("Parâmetros obrigatórios não informados");
      }

      const response = await fetch(`${apiUrl}/leads`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${apiToken}`,
        },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          productId,
          utm_source,
          utm_medium,
          utm_campaign,
          utm_term,
        }),
      });

      if (!response.ok) {
        throw new Error("Falha ao enviar a mensagem!");
      }

      setSuccessMessage("Mensagem enviada com sucesso!");
      reset();
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <>
        <FontLoader />
        <GlobalStyles />

        {error && (
          <Alert 
            type="error" 
            dismissible
            onDismiss={clearError}
            autoDismiss={5000}
          >
            {error!.message}
          </Alert>
        )}

        {successMessage && (
          <Alert 
            type="success"
            dismissible
            onDismiss={() => setSuccessMessage('')}
            autoDismiss={5000}
          >
            {successMessage}
          </Alert>
        )}

        <FormContainer ref={ref} >
          {showHeader && 
            <HeaderContainer>
              <Title $textColor={textColor}>Atendimento por mensagem</Title>

              <Text $textColor={textColor}>Informe seus dados e retornaremos a mensagem.</Text>
            </HeaderContainer>
          }

          <Form $textColor={textColor} onSubmit={handleSubmit(sendMessage)} noValidate>
            <Input
              id="name"
              label="Nome *"
              placeholder="Digite seu nome"
              {...register("name")}
              borderColor={colorPrimary}
              textColor={textColor}
              error={errors.name?.message}
              autoComplete="name"
              required
            />

            <Input
              id="email"
              label="Email *"
              type="email"
              placeholder="exemplo@email.com"
              {...register("email")}
              borderColor={colorPrimary}
              textColor={textColor}
              error={errors.email?.message}
              autoComplete="email"
              required
            />

            <Input
              id="phone"
              label="Telefone *"
              placeholder="(11) 00000-0000"
              mask="phone"
              {...register("phone")}
              borderColor={colorPrimary}
              textColor={textColor}
              error={errors.phone?.message}
              required
              value={phoneValue}
            />

            <h6>* Campos de preenchimento obrigatório.</h6>

            <ButtonContainer>
              <Button bgColor={colorPrimary} color={textColor} type="submit" isSubmitting={loading}>
                Enviar mensagem
              </Button>
            </ButtonContainer>

            <p>A Mitre Realty respeita a sua privacidade e utiliza os seus dados pessoais para contatá-lo por e-mail ou telefone aqui registrados. Para saber mais, acesse a nossa{ ' '}
            <a
              href="https://www.mitrerealty.com.br/politica-de-privacidade"
              target="_blank"
              rel="noopener noreferrer"
            >
              Política de Privacidade
            </a>. Ao clicar em {'"'}enviar{'"'}, você concorda em permitir que a Mitre Realty, armazene e processe os dados pessoais fornecidos por você para finalidade informada</p>

          </Form>
        </FormContainer>
      </>
  );
});

MitreFormComponent.displayName = "MitreFormComponent";

export default MitreFormComponent;
